<template>
  <div class="form-group">
    <label
      for="dropdown-select"
      aria-label="Select something"
      class="form-label"
    >
      {{ label }}
      <span v-if="required && label" class="required"></span>
      <span v-if="optionalLabel" class="optional-label">({{ optionalLabel }})</span>
    </label>
    <v-select
      :placeholder="placeholder"
      :class="[
        `dropdown-select`,
        error && `dropdown-select--error`,
        hover && `dropdown-select--hover`,
        disabled && `dropdown-select--disabled`,
        multiple && `dropdown-select--multiple`,
      ]"
      :components="{ Deselect }"
      :label="dropdownLabel"
      :multiple="multiple"
      :disabled="disabled"
      :close-on-select="closeOnSelect"
      :options="options"
      :filterable="filterable"
      :value="value"
      :loading="loading"
      :clearable="clearable"
      :selectable="(option) => option[dropdownLabel] !== disabledOption"
      @search="onSearch"
      @input="setSelected"
      @open="onOpen"
    >
      <template #search="{ attributes, events }">
        <input
          class="dropdown-select__input vs__search"
          v-bind="attributes"
          v-on="events"
        />
      </template>
      <template #selected-option-container="{ option, deselect }">
        <div
          v-tooltip="tooltipText(option, dropdownLabel)"
          class="vs__selected dropdown-select__values"
          aria-label="dropdown container"
        >
          <span
            :class="[
              'dropdown-select__values__content',
              { 'dropdown-select__values__content--multiple': multiple },
            ]"
          >{{ option[dropdownLabel] }}</span>
          <button
            type="button"
            title="deselect"
            aria-label="deselect"
            class="dropdown-select__values__button"
            @click="deselect(option)"
          >
            <icon
              v-if="multiple && !disabled"
              class-name="dropdown-select__values__button__icon"
              name="times-circle"
            />
          </button>
        </div>
      </template>
      <template #open-indicator="{ attributes }">
        <icon
          v-bind="attributes"
          class-name="dropdown-select__dropdown-icon"
          name="chevron-down"
        />
      </template>
      <template #spinner="{ loading }">
        <div
          v-if="loading"
          style="border-left-color: #42b0d5"
          class="vs__spinner"
          aria-label="loading spinner"
        ></div>
      </template>
      <template #no-options="{ searching }">
        <template v-if="searching">
          <em class="dropdown-select__no-results">No results found</em>
        </template>
        <em v-else class="dropdown-select__no-options">No Options</em>
      </template>
    </v-select>
    <p
      v-if="errorMessage"
      class="form-label form-label--error form-label--error--active"
      aria-label="Error Message"
    >
      {{ errorMessage }}
    </p>
    <div
      v-if="helpMessage"
      :class="[`form-help`, helpMessagePosition === `right` && `text-right`]"
      aria-label="Help Message"
    >
      {{ helpMessage }}
    </div>
    <div
      v-if="!isEmptyObject(customComponent)"
      v-tooltip="customComponent.tooltip"
      :style="{ width: 'max-content', 'max-width':'300px' }"
      v-html="customComponent.component"
    ></div>
  </div>
</template>

<script>
// Components
import Icon from "../Icon/Icon.vue";

// Importing Vue and Vue Select
import vSelect from "vue-select";

// Importing the Vue Select Styles
import "vue-select/dist/vue-select.css";


// Helpers
import { isEmptyObject } from "../../utils/helpers";

export default {
  name: "Dropdown",
  components: {
    vSelect,
    Icon,
  },
  props: {
    /**
     * @description
     * Provides the list of Select Options for Select Input
     */
    options: {
      type: Array,
      required: true,
    },
    /**
     * @description
     * Provides Label for Select Input
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Placeholder Text for Select Input
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Optional Label for Select Input
     */
    optionalLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Filter Name to the Dropdown in case it is being used with Filters
     * Useful to Sync Filter Configurations
     */
    filterName: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Dropdown Label for the Vue Select to Display the Label Names for the Options
     */
    dropdownLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Error Message for Select Input
     */
    errorMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Help Message for Select Input
     */
    helpMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Checks the Select Input help message position
     * Maersk Design System supports left and right
     */
    helpMessagePosition: {
      type: String,
      validator: (val) => ["left", "right"].includes(val),
      default: "left",
    },
    /**
     * @description
     * Emits the value change on input, Parent Component needs to sync values emitted
     */
    value: {
      type: [String, Object, Array],
      required: true,
    },
    /**
     * @description
     * Can the user clear the selected property?
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Flag to enable/disable loading state of the dropdown
     * If we are using APIs to filter data then this should be enabled/disabled while making APIs Requests
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Flag to enable/disable UI Filter of the Dropdown options
     * When loading server side options, it can be useful to disable the client side filtering
     */
    filterable: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Close a dropdown when an option is chosen.
     * Set to false to keep the dropdown open (useful when combined with multi-select)
     */
    closeOnSelect: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Provides Type of Select Input
     * Maersk Design System supports Default & Multiple Select
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Hoverable
     */
    hover: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should display Error Outline
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Option value should be Disabled
     */
    disabledOption: {
      type: String,
      default: "Select",
    },
    /**
     * @description
     * Checks if the Dropdown should display as Required
     */
    required: {
      type: Boolean,
      default: false,
    },
  /**
     * @description
     * Pass custom compoent as a string inside the object
     */
    customComponent: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    /**
     * @description
     * Customized Deselect icon
     */
    Deselect: {
      render: (createElement) =>
        createElement("span", {
          class: {
            "mi-20px": true,
            "mi-times-circle": true,
          },
        }),
    },
  }),
  methods: {
    /**
     * @description
     * The search event provides a hook to load options from a parent component when the search text is updated
     * Emits the Filter Search when user types 3 or more characters
     */
    onSearch(search) {
      this.$emit("onSearch", {
        type: "dropdown",
        filterName: this.filterName,
        searchValue: search,
        length: search.length,
      });
    },
    /**
     * @description
     * Emits the Selected Value along with the Filter Name (if any)
     */
    setSelected(value) {
      this.$emit("onChange", {
        type: "dropdown",
        filterName: this.filterName,
        value: value,
      });
    },
    /**
     * @description
     * Emits the when dropdown opens
     */
    onOpen() {
      this.$emit("onOpenDropdown");
    },
    /**
     * @description
     * Tooltip for selected values
     */
    tooltipText(item, dropdownLabel) {
      return item[dropdownLabel] || "";
    },
    isEmptyObject
  },
};
</script>

<style lang="scss">
.dropdown-select {
  & .vs__dropdown-toggle {
    height: 43px;
    padding-left: 1px !important;
    padding-right: 5px !important;

    &:hover {
      border: 1px solid var(--Gray-600);
    }

    & .dropdown-select__values {
      padding: 0.25rem !important;
      padding-left: 0.15rem !important;
    }
  }

  &--error {
    & .vs__dropdown-toggle {
      border: 1px solid var(--Danger) !important;
      &:focus {
        box-shadow: 0 0 0 3px var(--Danger-Light) !important;
      }
    }
  }

  &--hover {
    & .vs__dropdown-toggle {
      border: 1px solid var(--Gray-600);
    }
  }

  &--disabled {
    & .vs__dropdown-toggle {
      color: var(--Gray-500);
    }
  }

  &--multiple {
    .vs__dropdown-toggle {
      min-height: 43px;
      height: auto !important;
    }
  }

  &__values {
    &__button {
      background-color: Transparent;
      background-repeat: no-repeat;
      border: none;
      cursor: pointer;
      overflow: hidden;
    }
    &__content {
      overflow: hidden;
      text-overflow: ellipsis;
      &--multiple {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 70px;
      }
    }
  }

  .required:after {
    content: " *";
    color: var(--Danger);
  }
}

.vs--open {
  & .vs__dropdown-toggle {
    border: 1px solid var(--Blue-400) !important;
    box-shadow: 0 0 0 3px var(--Info-Light) !important;
  }
}

.vs__dropdown-menu {
  li {
    white-space: initial;
  }
}

.vs__selected {
  color: var(--Gray-800);
}

.vs__selected-options {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
